/**
 * Utenti, ruoli e accesso con un provider aziendale.
 *
 * ## Perché una sezione propria
 *
 * Erano in fondo a «Impostazioni», fra la licenza e la password propria: chi
 * cercava «dove aggiungo un collega» doveva scorrere impostazioni che non
 * c'entrano, e chi cercava l'accesso aziendale non lo trovava affatto — non
 * esisteva.
 *
 * ## Le tre cose che stanno qui, e perché insieme
 *
 * Creare un'utenza, abilitarne una che esiste già, e collegare l'IdP
 * dell'azienda sono tre modi di rispondere alla stessa domanda: **chi può
 * entrare**. Tenerle in tre posti diversi obbliga a sapere in anticipo quale
 * dei tre casi si ha davanti.
 *
 * ## I ruoli personalizzati
 *
 * Accanto ai tre ruoli del prodotto, quelli del tenant: un ruolo di partenza
 * più un livello per ogni voce del menu (components/RuoliPersonalizzati.tsx).
 * Si assegnano dallo stesso selettore del ruolo, nell'elenco degli utenti.
 */
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { api } from '../api/client'
import type { IdentityProvider, RuoloPersonalizzato, User } from '../api/types'
import { PageSection,
  SectionedPage,
  Card,
  EmptyState,
  ErrorBanner,
  formatDateTime,
  Spinner,
  StatusBadge,
  etichettaStato,
} from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { LinkPassword } from '../components/LinkPassword'
import { useAsync } from '../hooks/useAsync'
import { useAuth } from '../auth/AuthContext'
import { RuoliPersonalizzati, nomeDelRuolo } from '../components/RuoliPersonalizzati'
import { t } from '../i18n/lingua'

//: Lunghezza minima di una password creata da qui.
//:
//: Dieci e non otto: è la soglia sotto la quale una password si indovina con
//: una lista, e questa console apre l'inventario di una rete intera.
const MIN_PASSWORD = 10

// Una funzione e non una costante di modulo: `t` va chiamata quando i ruoli si
// mostrano, non quando il file si importa.
function ruoli() {
  return [
    { value: 'tenant_admin', label: t('Amministratore'), hint: t('Gestisce utenti, credenziali e sonde') },
    { value: 'tenant_operator', label: t('Operatore'), hint: t('Avvia discovery e consulta i dati') },
    { value: 'read_only', label: t('Sola lettura'), hint: t('Può solo consultare') },
  ]
}


export default function Users() {
  const { principal } = useAuth()
  const users = useAsync(() => api.users(), [])
  const personalizzati = useAsync(() => api.customRoles(), [])
  const isAdmin =
    principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin')

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Utenti')}</h1>
          <p className="page__subtitle">
            {t('Chi può entrare in questa console, con quale ruolo e da dove')}
          </p>
        </div>
      </header>

      {!isAdmin ? (
        <Card title={t('Utenti')}>
          <EmptyState
            title={t('Gestione riservata agli amministratori')}
            hint={t('Chiedi a un amministratore del tenant di modificare gli accessi.')}
          />
        </Card>
      ) : (
        <>
          <SectionedPage
            sections={[
              { id: 'abilitati', label: t('Utenti abilitati') },
              { id: 'ruoli', label: t('Ruoli e permessi') },
              { id: 'nuovo', label: t('Nuovo utente') },
              { id: 'abilita', label: t('Abilita utenza') },
              { id: 'sso', label: t('Accesso aziendale') },
            ]}
          >
          <PageSection id="abilitati">
            <UserList state={users} currentUserId={principal?.subject} personalizzati={personalizzati.data ?? []} />
          </PageSection>
          <PageSection id="ruoli">
            <RuoliPersonalizzati
              onChanged={() => {
                void personalizzati.reload()
                void users.reload()
              }}
            />
          </PageSection>
          <PageSection id="nuovo">
            <NewUser onCreated={users.reload} />
          </PageSection>
          <PageSection id="abilita">
            <InviteMember onInvited={users.reload} />
          </PageSection>
          <PageSection id="sso">
            <SingleSignOn />
          </PageSection>
          </SectionedPage>
        </>
      )}
    </>
  )
}

function UserList({
  state,
  currentUserId,
  personalizzati,
}: {
  state: ReturnType<typeof useAsync<User[]>>
  currentUserId?: string
  personalizzati: RuoloPersonalizzato[]
}) {
  const [error, setError] = useState<unknown>(null)
  // «Link password»: mailed to the person, or shown here (accounts of this tenant only).
  const [linkPer, setLinkPer] = useState<User | null>(null)

  // Nel selettore i ruoli personalizzati hanno il valore `custom:<id>`: uno
  // solo per utente, come i ruoli del prodotto.
  async function changeRole(user: User, role: string) {
    setError(null)
    try {
      await api.updateUser(
        user.id,
        role.startsWith('custom:') ? { custom_role_id: role.slice(7) } : { roles: [role] },
      )
      state.reload()
    } catch (err) {
      setError(err)
    }
  }

  async function toggleStatus(user: User) {
    setError(null)
    try {
      await api.updateUser(user.id, { status: user.status === 'active' ? 'disabled' : 'active' })
      state.reload()
    } catch (err) {
      setError(err)
    }
  }

  async function remove(user: User) {
    if (!window.confirm(t('Eliminare l’utente {email} da questo tenant? Perde subito l’accesso. Non si può annullare.', { email: user.email }))) return
    setError(null)
    try {
      await api.deleteUser(user.id)
      state.reload()
    } catch (err) {
      setError(err)
    }
  }

  // The role as text, for search, filters, sort and CSV.
  const nomeRuolo = (u: User) =>
    u.custom_role_id
      ? nomeDelRuolo(personalizzati.find((r) => r.id === u.custom_role_id) ?? { name: '' })
      : ruoli().find((r) => r.value === u.roles[0])?.label ?? u.roles[0] ?? ''

  const colonne: Column<User>[] = [
    { key: 'email', header: t('Email'), render: (u) => (
      <>
        {u.email}
        {u.id === currentUserId && <span className="chip">{t('tu')}</span>}
        {/* Un'utenza di un altro tenant si può abilitare e revocare, ma non
            modificare: dirlo qui evita di cercare un pulsante che non c'è. */}
        {!u.owned_by_tenant && (
          <span className="chip" title={t("L'utenza appartiene a un altro tenant")}>
            {t('ospite')}
          </span>
        )}
      </>
    ) },
    { key: 'display_name', header: t('Nome'), value: (u) => u.display_name ?? '' },
    { key: 'role', header: t('Ruolo su questo tenant'), value: nomeRuolo, render: (u) => (
      <select
        value={u.custom_role_id ? `custom:${u.custom_role_id}` : (u.roles[0] ?? '')}
        onChange={(e) => changeRole(u, e.target.value)}
        style={{ minWidth: 150 }}
      >
        <optgroup label={t('Ruoli del prodotto')}>
          {ruoli().map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </optgroup>
        {personalizzati.some((r) => r.preset) && (
          <optgroup label={t('Ruoli predefiniti di CENSO')}>
            {personalizzati.filter((r) => r.preset).map((r) => (
              <option key={r.id} value={`custom:${r.id}`}>
                {nomeDelRuolo(r)}
              </option>
            ))}
          </optgroup>
        )}
        {personalizzati.some((r) => !r.preset) && (
          <optgroup label={t('Ruoli personalizzati')}>
            {personalizzati.filter((r) => !r.preset).map((r) => (
              <option key={r.id} value={`custom:${r.id}`}>
                {r.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    ) },
    { key: 'status', header: t('Accesso'), value: (u) => etichettaStato(u.status) ?? u.status, render: (u) => <StatusBadge status={u.status} /> },
    { key: 'created_at', header: t('Abilitato il'), type: 'date', render: (u) => formatDateTime(u.created_at) },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (u) => {
      const self = u.id === currentUserId
      return (
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          {u.auth_source === 'local' && (
            <><button className="secondary" onClick={() => setLinkPer(u)} disabled={u.status !== 'active'}
                      title={t('Un collegamento per scegliere la password: per mail o da consegnare')}>
              {t('Link password')}
            </button>{' '}</>
          )}
          <button className="secondary" onClick={() => toggleStatus(u)} disabled={self}>
            {u.status === 'active' ? t('Disabilita') : t('Riattiva')}
          </button>{' '}
          <button className="danger" onClick={() => remove(u)} disabled={self}>
            {u.owned_by_tenant ? t('Elimina') : t('Revoca accesso')}
          </button>
        </div>
      )
    } },
  ]

  return (
    <Card title={t('Utenti abilitati su questo tenant')}>
      <ErrorBanner error={error ?? state.error} />
      {state.loading && !state.data ? (
        <Spinner />
      ) : !state.data?.length ? (
        <EmptyState title={t('Nessun utente')} />
      ) : (
        <DataTable rows={state.data} columns={colonne} rowKey={(u) => u.id} conteggio={{ nome: t('utenti') }} />
      )}
      {linkPer && (
        <LinkPassword
          email={linkPer.email}
          possoMostrare={linkPer.owned_by_tenant}
          perchéNo={t('Il link si può mostrare solo per le utenze di questo tenant: questa persona appartiene a un altro tenant, e chi ha il link avrebbe anche quell’accesso. Si può solo mandarlo per mail.')}
          manda={() => api.sendPasswordLink(linkPer.id)}
          mostra={() => api.showPasswordLink(linkPer.id)}
          onChiudi={() => setLinkPer(null)}
        />
      )}
      <p className="field__hint" style={{ marginTop: 12 }}>
        {t('Ruolo e accesso valgono su questo tenant: la stessa utenza può averne altri altrove. L’ultimo amministratore attivo non può essere rimosso o disabilitato, perché il tenant resterebbe senza nessuno in grado di gestirlo.')}
      </p>
    </Card>
  )
}


function NewUser({ onCreated }: { onCreated: () => void }) {
  // Due modi di far nascere un'utenza. «Invita» è quello giusto per una persona
  // vera: la password la sceglie lei, da un collegamento a scadenza — chi la
  // crea non la conosce, e per ogni registro non «è» quell'utente. «Imposta una
  // password» resta per gli account di servizio, dove non c'è una casella dove
  // mandare un invito.
  const [modo, setModo] = useState<'invito' | 'password'>('invito')
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [scadenzaOre, setScadenzaOre] = useState('72')
  const [defaultOre, setDefaultOre] = useState<number | null>(null)
  const [role, setRole] = useState('tenant_operator')
  const [error, setError] = useState<unknown>(null)
  const [fatto, setFatto] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // La scadenza predefinita del tenant: seme del campo, e riferimento per dire
  // «questo invito scade prima/dopo del predefinito».
  useEffect(() => {
    api
      .inviteSettings()
      .then((s) => {
        setDefaultOre(s.expiry_hours)
        setScadenzaOre(String(s.expiry_hours))
      })
      .catch(() => {})
  }, [])

  async function salvaPredefinito() {
    setError(null)
    setFatto(null)
    try {
      const s = await api.setInviteSettings(Number(scadenzaOre) || 72)
      setDefaultOre(s.expiry_hours)
      setScadenzaOre(String(s.expiry_hours))
      setFatto(t('Scadenza predefinita salvata per i prossimi inviti.'))
    } catch (err) {
      setError(err)
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setFatto(null)
    setBusy(true)
    try {
      if (modo === 'invito') {
        await api.inviteNewUser({
          email,
          display_name: displayName || null,
          roles: [role],
          expiry_hours: Number(scadenzaOre) || undefined,
        })
        setFatto(t('Invito inviato a {email}.', { email }))
      } else {
        await api.createUser({
          email,
          display_name: displayName || null,
          password,
          roles: [role],
        })
        setFatto(t('Utenza creata.'))
      }
      setEmail('')
      setDisplayName('')
      setPassword('')
      onCreated()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Nuovo utente')}>
      <ErrorBanner error={error} />
      {fatto && <div className="banner banner--info">{fatto}</div>}
      <form onSubmit={onSubmit}>
        {/* Il modo si sceglie prima del resto: cambia quali campi hanno senso. */}
        <div className="field">
          <label>{t('Come dare accesso')}</label>
          <div className="rowactions" style={{ justifyContent: 'flex-start', gap: 16 }}>
            <label className="text-muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="radio"
                name="modo-utente"
                checked={modo === 'invito'}
                onChange={() => setModo('invito')}
              />
              {t('Invita via email (la password la sceglie lui)')}
            </label>
            <label className="text-muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="radio"
                name="modo-utente"
                checked={modo === 'password'}
                onChange={() => setModo('password')}
              />
              {t('Imposta una password')}
            </label>
          </div>
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="email">{t('Email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="dn">{t('Nome')}</label>
            <input
              id="dn"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('Nome Cognome')}
            />
          </div>
          {modo === 'password' ? (
            <div className="field">
              <label htmlFor="pw">{t('Password iniziale')}</label>
              <input
                id="pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={MIN_PASSWORD}
                autoComplete="new-password"
                required
              />
            </div>
          ) : (
            <div className="field">
              <label htmlFor="scad">{t('Scadenza invito (ore)')}</label>
              <input
                id="scad"
                type="number"
                min={1}
                max={720}
                value={scadenzaOre}
                onChange={(e) => setScadenzaOre(e.target.value)}
              />
              <p className="field__hint">
                {defaultOre != null
                  ? t('Durata predefinita per il tenant: {ore} ore. Trascorso questo intervallo, il collegamento scade e non è più utilizzabile.', {
                      ore: defaultOre,
                    })
                  : t('Dopo, il collegamento non vale più e serve un nuovo invito.')}
              </p>
              {defaultOre != null && Number(scadenzaOre) !== defaultOre && (
                <button
                  type="button"
                  className="linklike"
                  onClick={salvaPredefinito}
                  style={{ alignSelf: 'flex-start', padding: 0 }}
                >
                  {t('Salva come predefinito')}
                </button>
              )}
            </div>
          )}
          <div className="field">
            <label htmlFor="role">{t('Ruolo')}</label>
            <select id="role" value={role} onChange={(e) => setRole(e.target.value)}>
              {ruoli().map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <p className="field__hint">{ruoli().find((r) => r.value === role)?.hint}</p>
          </div>
        </div>
        <button type="submit" disabled={busy}>
          {busy ? t('Attendere…') : modo === 'invito' ? t('Invita utente') : t('Crea utente')}
        </button>
      </form>
    </Card>
  )
}


/**
 * Abilita su questo tenant un'utenza che esiste già altrove.
 *
 * È separato dalla creazione perché sono due cose diverse: creare fa nascere
 * un'identità con una password, invitare dà accesso a un'identità che non è di
 * questo tenant e le cui credenziali non vanno toccate da qui.
 */
function InviteMember({ onInvited }: { onInvited: () => void }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('tenant_operator')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.inviteMember({ email: email.trim(), roles: [role] })
      setEmail('')
      onInvited()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t("Abilita un'utenza esistente")}>
      <ErrorBanner error={error} />
      <form onSubmit={onSubmit}>
        <div className="row">
          <div className="field">
            <label htmlFor="invite-email">{t('Email')}</label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <p className="field__hint">
              {t('L’utenza continua ad accedere con le proprie credenziali e può ora selezionare anche questo tenant.')}
            </p>
          </div>
          <div className="field">
            <label htmlFor="invite-role">{t('Ruolo su questo tenant')}</label>
            <select id="invite-role" value={role} onChange={(e) => setRole(e.target.value)}>
              {ruoli().map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button type="submit" disabled={busy}>
          {busy ? t('Abilitazione…') : t('Abilita')}
        </button>
      </form>
    </Card>
  )
}


/**
 * Collegare il provider di identità dell'azienda.
 *
 * ## Che cosa fa, detto prima di tutto il resto
 *
 * Verifica **chi sei**, non concede l'accesso. Chi si autentica entra solo se
 * qualcuno lo ha già invitato qui — la stessa regola della password. È la prima
 * cosa che la scheda dice, perché è la sorpresa più probabile: chi collega un
 * IdP si aspetta che i colleghi entrino da soli.
 *
 * ## Perché l'issuer e non i tre indirizzi
 *
 * Perché cambiano quando il provider li cambia, e un valore ricopiato a mano
 * resta indietro senza che nessuno lo sappia: l'accesso smette di funzionare
 * con un errore che parla d'altro. Dall'issuer si scoprono da soli, e si
 * verifica subito che risponda — un pulsante «entra con…» che porta a un errore
 * non aiuta nessuno.
 */
function SingleSignOn() {
  const providers = useAsync(() => api.identityProviders(), [])
  const [modifica, setModifica] = useState<IdentityProvider | null>(null)
  const [aperto, setAperto] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [prova, setProva] = useState<{
    nome: string
    ok: boolean
    problems: string[]
    discovered: Record<string, string | null>
    not_checked: string[]
  } | null>(null)

  async function verifica(p: IdentityProvider) {
    setError(null)
    setProva(null)
    try {
      setProva({ nome: p.name, ...(await api.testIdentityProvider(p.id)) })
    } catch (err) {
      setError(err)
    }
  }

  async function elimina(p: IdentityProvider) {
    if (
      !window.confirm(
        `${t('Scollegare «{nome}»?', { nome: p.name })}\n\n` +
          t('Gli utenti restano: sono abilitati per invito, e il provider diceva solo chi fossero. Chi ha una password continua a entrare; chi non ce l’ha dovrà farsene dare una.'),
      )
    )
      return
    setError(null)
    try {
      await api.deleteIdentityProvider(p.id)
      providers.reload()
    } catch (err) {
      setError(err)
    }
  }

  const colonne: Column<IdentityProvider>[] = [
    { key: 'name', header: t('Nome') },
    { key: 'domains', header: t('Domini'), mono: true, value: (p) => p.domains.join(', ') },
    { key: 'issuer', header: t('Issuer'), mono: true, value: (p) => p.issuer ?? '', render: (p) => <span style={{ fontSize: 12 }}>{p.issuer}</span> },
    { key: 'enabled', header: t('Stato'), value: (p) => (p.enabled ? t('Attivo') : t('Disattivato')), render: (p) => (
      <>
        <span className={`badge badge--${p.enabled ? 'ok' : 'muted'}`}>
          {p.enabled ? t('Attivo') : t('Disattivato')}
        </span>
        {/* Distinto da «configurato»: un provider senza segreto è salvato e
            non funziona, e senza questa riga il motivo andrebbe cercato al
            primo accesso fallito. */}
        {!p.has_client_secret && (
          <span className="chip" title={t('Alcuni provider lo richiedono')}>
            {t('senza segreto')}
          </span>
        )}
      </>
    ) },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (p) => (
      <>
        <button type="button" className="link" onClick={() => verifica(p)}>
          {t('Verifica')}
        </button>{' '}
        <button
          type="button"
          className="link"
          onClick={() => {
            setModifica(p)
            setAperto(true)
          }}
        >
          {t('Modifica')}
        </button>{' '}
        <button type="button" className="link" onClick={() => elimina(p)}>
          {t('Scollega')}
        </button>
      </>
    ) },
  ]

  return (
    <Card title={t('Accesso con il provider aziendale (OpenID Connect)')}>
      <ErrorBanner error={error ?? providers.error} />

      <div className="banner banner--info">
        {t('Il provider verifica chi è chi entra, non se può entrare: chi si autentica accede solo se il suo indirizzo è già fra gli utenti qui sopra. Collegare l’IdP non apre la console a tutta l’azienda.')}
      </div>

      {providers.loading && !providers.data ? (
        <Spinner />
      ) : !providers.data?.length ? (
        <EmptyState
          title={t('Nessun provider collegato')}
          hint={t('Chi accede lo fa con indirizzo e password. Collegando un IdP, gli utenti già abilitati entrano con le credenziali aziendali.')}
        />
      ) : (
        <DataTable rows={providers.data} columns={colonne} rowKey={(p) => p.id} conteggio={{ nome: t('provider') }} />
      )}

      {/* L'esito della verifica, con ciò che **non** ha potuto controllare: un
          verde che si legge come «funziona tutto» manda a cercare altrove il
          giorno che l'accesso fallisce per il segreto sbagliato. */}
      {prova && (
        <div className={`banner banner--${prova.ok ? 'ok' : 'warn'}`}>
          <strong>
            {prova.ok
              ? t('«{nome}»: la configurazione risponde.', { nome: prova.nome })
              : t('«{nome}»: qualcosa non torna.', { nome: prova.nome })}
          </strong>
          {prova.problems.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: '1.2em' }}>
              {prova.problems.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          {prova.ok && (
            <p style={{ margin: '6px 0 0' }} className="mono">
              {prova.discovered.authorization_endpoint}
            </p>
          )}
          <p style={{ margin: '8px 0 0' }}>
            {t('Non verificabile da qui, si vede solo a un accesso vero: {elenco}.', {
              elenco: prova.not_checked.join(' · '),
            })}
          </p>
          <button
            type="button"
            className="secondary"
            style={{ marginTop: 8 }}
            onClick={() => setProva(null)}
          >
            {t('Chiudi')}
          </button>
        </div>
      )}

      {aperto ? (
        <ProviderForm
          provider={modifica}
          onDone={() => {
            setAperto(false)
            setModifica(null)
            providers.reload()
          }}
          onCancel={() => {
            setAperto(false)
            setModifica(null)
          }}
        />
      ) : (
        <div className="rowactions" style={{ textAlign: 'left', marginTop: 12 }}>
          <button
            className="secondary"
            onClick={() => {
              setModifica(null)
              setAperto(true)
            }}
          >
            {t('Collega un provider')}
          </button>
        </div>
      )}
    </Card>
  )
}

function ProviderForm({
  provider,
  onDone,
  onCancel,
}: {
  provider: IdentityProvider | null
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(provider?.name ?? '')
  const [issuer, setIssuer] = useState(provider?.issuer ?? '')
  const [clientId, setClientId] = useState(provider?.client_id ?? '')
  const [clientSecret, setClientSecret] = useState('')
  const [domains, setDomains] = useState((provider?.domains ?? []).join(', '))
  const [gruppi, setGruppi] = useState<RigaGruppo[]>(
    Object.entries(provider?.role_mapping ?? {}).map(([gruppo, ruolo]) => ({
      gruppo,
      ruolo,
    })),
  )
  const [enabled, setEnabled] = useState(provider?.enabled ?? true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [copiato, setCopiato] = useState(false)

  // L'indirizzo di ritorno, calcolato **qui** e mostrato da copiare.
  //
  // È il pezzo che rende la configurazione possibile: l'IdP lo confronta con
  // quello registrato, e uno diverso — anche di uno slash — fa fallire l'accesso
  // con «redirect_uri mismatch». Senza vederlo, chi configura deve indovinarlo,
  // e sbaglia: è la ragione per cui questa schermata prima non funzionava.
  const redirectUri = `${window.location.origin}/login`

  async function salva(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const role_mapping: Record<string, string> = {}
      for (const riga of gruppi) {
        if (riga.gruppo.trim()) role_mapping[riga.gruppo.trim()] = riga.ruolo
      }
      const payload = {
        name: name.trim(),
        issuer: issuer.trim(),
        client_id: clientId.trim(),
        // Vuoto significa «lascia quello che c'era»: il segreto non torna mai
        // nelle letture, quindi chi modifica il nome non ce l'ha da rimandare —
        // e senza questa regola lo cancellerebbe.
        client_secret: clientSecret.trim() || null,
        domains: domains.split(',').map((d) => d.trim()).filter(Boolean),
        role_mapping,
        enabled,
      }
      if (provider) await api.updateIdentityProvider(provider.id, payload)
      else await api.createIdentityProvider(payload)
      onDone()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={salva} style={{ marginTop: 16 }}>
      <ErrorBanner error={error} />

      {/* Prima dei campi, non dopo: è la cosa da fare **sull'IdP**, e chi
          compila questo modulo senza averla fatta ottiene una configurazione
          che si salva e non funziona. */}
      <div className="banner banner--info">
        <strong>{t('Primo passo, sul tuo provider.')}</strong>{' '}
        {t('Registra un’applicazione web e incolla questo come indirizzo di ritorno (redirect URI, callback URL):')}
        <pre
          className="mono"
          style={{ margin: '8px 0', padding: 8, overflowX: 'auto' }}
        >
          {redirectUri}
        </pre>
        <button
          type="button"
          className="secondary"
          onClick={() => {
            navigator.clipboard?.writeText(redirectUri)
            setCopiato(true)
          }}
        >
          {copiato ? t('Copiato') : t('Copia l’indirizzo di ritorno')}
        </button>
        <p style={{ margin: '10px 0 0' }}>
          {t('Deve corrispondere esattamente, anche nello slash finale: uno diverso fa fallire l’accesso con «redirect_uri mismatch». Poi torna qui con issuer, client ID e client secret.')}
        </p>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="idp-name">{t('Nome')}</label>
          <input
            id="idp-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('Microsoft Entra ID')}
            required
          />
          <p className="field__hint">
            {t('È quello che compare sul pulsante: «Entra con…».')}
          </p>
        </div>
        <div className="field" style={{ flex: 2 }}>
          <label htmlFor="idp-issuer">{t('Issuer')}</label>
          <input
            id="idp-issuer"
            className="mono"
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
            placeholder="https://login.microsoftonline.com/<id-tenant>/v2.0"
            required
          />
          <p className="field__hint">
            {/* Gli indirizzi si scoprono da qui: ricopiarli a mano li fa restare
                indietro quando il provider li cambia. */}
            {t('Da qui si leggono da soli gli indirizzi del provider — non serve copiarli. Viene verificato al salvataggio: se non risponde, non si salva.')}{' '}
            <ProviderEsempi />
          </p>
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="idp-client">{t('Client ID')}</label>
          <input
            id="idp-client"
            className="mono"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="idp-secret">{t('Client secret')}</label>
          <input
            id="idp-secret"
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={provider?.has_client_secret ? t('•••••• (invariato)') : ''}
            autoComplete="new-password"
          />
          <p className="field__hint">
            {provider?.has_client_secret
              ? t('C’è già. Lascialo vuoto per non cambiarlo.')
              : t('Alcuni provider lo richiedono, altri no.')}{' '}
            {t('Non si rilegge mai: se lo perdi, ne generi un altro sul provider.')}
          </p>
        </div>
      </div>

      <div className="field">
        <label htmlFor="idp-domains">{t('Domini di posta')}</label>
        <input
          id="idp-domains"
          className="mono"
          value={domains}
          onChange={(e) => setDomains(e.target.value)}
          placeholder={t('azienda.it, azienda.com')}
          required
        />
        <p className="field__hint">
          {/* Il dominio è ciò che fa comparire il pulsante: senza, l'IdP è
              configurato e irraggiungibile. */}
          {t('Separati da virgola, senza la chiocciola. Chi scrive un indirizzo con uno di questi domini vede comparire il pulsante nella pagina di accesso. Un dominio può appartenere a un solo provider.')}
        </p>
      </div>

      {/* Le righe al posto del JSON: chiedere di scrivere JSON a mano in un
          campo di testo significa che una virgola di troppo diventa un errore
          di sintassi al salvataggio, su una cosa che è un elenco di coppie. */}
      <div className="field">
        <label>{t('Gruppi del provider → ruolo (facoltativo)')}</label>
        {gruppi.map((riga, i) => (
          <div className="row" key={i} style={{ alignItems: 'flex-end' }}>
            <div className="field" style={{ flex: '1 1 200px' }}>
              <input
                className="mono"
                value={riga.gruppo}
                placeholder={t('censo-amministratori')}
                onChange={(e) =>
                  setGruppi(
                    gruppi.map((r, j) =>
                      j === i ? { ...r, gruppo: e.target.value } : r,
                    ),
                  )
                }
              />
            </div>
            <div className="field" style={{ flex: '0 1 220px' }}>
              <select
                value={riga.ruolo}
                onChange={(e) =>
                  setGruppi(
                    gruppi.map((r, j) =>
                      j === i ? { ...r, ruolo: e.target.value } : r,
                    ),
                  )
                }
              >
                {ruoli().map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flex: '0 0 auto' }}>
              <button
                type="button"
                className="link"
                onClick={() => setGruppi(gruppi.filter((_, j) => j !== i))}
              >
                {t('Togli')}
              </button>
            </div>
          </div>
        ))}
        <div className="rowactions" style={{ textAlign: 'left' }}>
          <button
            type="button"
            className="secondary"
            onClick={() =>
              setGruppi([...gruppi, { gruppo: '', ruolo: 'tenant_operator' }])
            }
          >
            {t('Aggiungi un gruppo')}
          </button>
        </div>
        <p className="field__hint">
          {/* Il mapping non concede: è un suggerimento a chi abilita. Dirlo qui
              evita di crederlo una regola di accesso. */}
          {t('Non concede l’accesso — quello resta l’invito — ma propone un ruolo quando abiliti un’utenza che il provider conosce già.')}
        </p>
      </div>

      <div className="checkbox">
        <input
          id="idp-enabled"
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <label htmlFor="idp-enabled">
          {t('Attivo')}
          <small>
            {t("Disattivarlo ferma l'accesso con il provider senza perdere la configurazione: è il gesto giusto mentre l'IdP è in manutenzione.")}
          </small>
        </label>
      </div>

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button type="submit" disabled={busy}>
          {busy ? t('Verifica del provider…') : provider ? t('Salva') : t('Collega')}
        </button>
        <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
          {t('Annulla')}
        </button>
      </div>
    </form>
  )
}

type RigaGruppo = { gruppo: string; ruolo: string }

/**
 * Dove trovare l'issuer sui provider più diffusi.
 *
 * Non è documentazione per completezza: «issuer» è il campo che si sbaglia più
 * spesso, perché ogni provider lo chiama in un modo e nessuno lo espone con
 * quel nome. Tre esempi tolgono la ricerca a chi ne usa uno dei tre.
 */
function ProviderEsempi() {
  const [aperto, setAperto] = useState(false)
  if (!aperto) {
    return (
      <button type="button" className="link" onClick={() => setAperto(true)}>
        {t("Dov'è l'issuer?")}
      </button>
    )
  }
  return (
    <span>
      <br />
      <strong>{t('Microsoft Entra ID:')}</strong>{' '}
      <span className="mono">
        https://login.microsoftonline.com/&lt;id-directory&gt;/v2.0
      </span>
      <br />
      <strong>{t('Google Workspace:')}</strong>{' '}
      <span className="mono">https://accounts.google.com</span>
      <br />
      <strong>{t('Keycloak:')}</strong>{' '}
      <span className="mono">https://&lt;host&gt;/realms/&lt;realm&gt;</span>
      <br />
      <strong>{t('Okta:')}</strong>{' '}
      <span className="mono">https://&lt;dominio&gt;.okta.com</span>
      <br />
      {t('In generale è l’indirizzo a cui, aggiungendo {percorso}, si ottiene un documento JSON.', {
        percorso: '/.well-known/openid-configuration',
      })}
    </span>
  )
}
